import { ReactNode, useEffect, useState } from "react";
import { ReactComponent as ArrowLeft } from "../../../../assets/Sessions/arrow-left.svg";
import { ISO639Lang } from "../../../../constans/languagesList";
import { Project } from "../../../../types/Company";
import {
  FlexPassSubscription,
  SubscriptionType,
} from "../../../../types/FlexPassTypes";
import {
  RescheduleRules,
  SeessionErrorLogs,
  SessionLog,
  SessionPackage,
  SessionType,
} from "../../../../types/TypeSession";
import { SpecialistTag } from "../../../../types/TypeSpecialist";
import { TypeUserData } from "../../../../types/TypeUsers";
import { formatDateAndTime } from "../../../../utils/dateActions";
import { getTranslation } from "../../../../utils/getTranslation";
import { flexPassTypeStrings } from "../../../FlexPass/flexPassUtils";
import SessionLogs from "../SessionLogs/SessionLogs";
import s from "./SessionInfoBlock.module.css";

type InfoCardData = {
  label: string;
  value: ReactNode;
};

export type LogEntry = {
  participant: {
    name: string;
    userId: string;
  };
  logs: { joinTime: string; leaveTime: string | null }[];
};

export type SessionForDisplay = {
  _id: string;
  subject?: string;
  sessionType?: SessionType;
  session_loggings: SessionLog[];
  sessionDuration?: { user: string; totalSessionTime: number }[];
  tags?: SpecialistTag[];
  title?: { text: string; language: string }[];
  description?: { text: string; language: string }[];
  packageRef?: (SessionPackage & { createdBy: TypeUserData }) | null;
  project?: Project;
  flexPassSubscription?: FlexPassSubscription;
  clients?: TypeUserData[];
  specialists?: TypeUserData[];
  dateAndTime?: { date: Date | string; duration: number };
  datesAndTime?: { date: Date | string; duration: number }[];
  clientCost?: number;
  specialistPay?: number;
  sessionCost?: number;
  rescheduleHistory?: {
    invokedBy?: string;
    rule: RescheduleRules;
    isThresholdPassed: boolean;
    invokedAt: string;
    previousDate?: string;
    newDate?: string;
  }[];
  errorLogs?: SeessionErrorLogs[];
};

type SessionInfoBlockProps = {
  session: SessionForDisplay;
  onBack: () => void;
  renderActions?: () => ReactNode;
  statusBadge?: string;
};

const moneyFormatter = new Intl.NumberFormat("en-US", {
  style: "currency",
  currency: "USD",
  minimumFractionDigits: 0,
  maximumFractionDigits: 2,
});

const sessionTypeStrings: Record<string, string> = {
  [SessionType.BARTER]: "Barter",
  [SessionType.CATALOG]: "Catalog",
  [SessionType.FLEX_PASS]: "Flex Pass",
  [SessionType.PROJECT]: "Project",
  [SessionType.SELECTION]: "Selection",
  [SessionType.SUBSCRIPTION]: "Subscription",
  [SessionType.REAL_TIME]: "Real Time",
};

const tagsSteps = ["Direction", "Topic", "Result"];

const getDisplayText = (value?: string | null) =>
  value && value.trim().length ? value : "-";

const formatDisplayDate = (value?: Date | string) => {
  if (!value) return "-";
  return formatDateAndTime(new Date(value), "en").formattedDate;
};

const formatDisplayDateTime = (value?: Date | string) => {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  const { formattedDate, formattedTime } = formatDateAndTime(date, "en");
  return `${formattedDate}, ${formattedTime}`;
};

const formatMoney = (amount?: number) =>
  typeof amount === "number" ? moneyFormatter.format(amount / 100) : "-";

const formatStack = (stack?: string) => {
  if (!stack) return "-";

  try {
    return JSON.stringify(JSON.parse(stack), null, 2);
  } catch {
    return stack;
  }
};

const getParticipantDisplayName = (
  userId: string | undefined,
  participants: TypeUserData[],
) => {
  if (!userId) return "-";
  const participant = participants.find((item) => item._id === userId);

  return participant
    ? `${getTranslation(participant.name)} ${getTranslation(participant.surname)}`
    : userId;
};

const transformLogs = (
  logs: SessionLog[],
  participants: TypeUserData[],
): LogEntry[] => {
  const groupedLogs = logs.reduce<Record<string, SessionLog[]>>((acc, log) => {
    if (!acc[log.invokedBy]) {
      acc[log.invokedBy] = [];
    }
    acc[log.invokedBy].push(log);
    return acc;
  }, {});

  return Object.entries(groupedLogs).map(([participantId, pLogs]) => {
    const participant = participants.find((p) => p._id === participantId);
    const name = participant
      ? `${getTranslation(participant.name)} ${getTranslation(participant.surname)}`
      : "Unknown Participant";
    const userId = participant ? participant._id : participantId;

    const sortedLogs = pLogs.sort(
      (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime(),
    );

    const sessionTimes: { joinTime: string; leaveTime: string | null }[] = [];
    let currentJoinTime: string | null = null;

    for (const log of sortedLogs) {
      if (log.action === "joinSession") {
        currentJoinTime = log.date;
      } else if (log.action === "leaveSession" && currentJoinTime) {
        sessionTimes.push({ joinTime: currentJoinTime, leaveTime: log.date });
        currentJoinTime = null;
      }
    }

    return {
      participant: { name, userId },
      logs: sessionTimes,
    };
  });
};

const SessionInfoBlock = ({
  session,
  onBack,
  renderActions,
  statusBadge,
}: SessionInfoBlockProps) => {
  const [sessionLogs, setSessionLogs] = useState<LogEntry[]>([]);
  const [isErrorLogsOpen, setIsErrorLogsOpen] = useState(false);

  useEffect(() => {
    const participants: TypeUserData[] = [
      ...(session.clients ?? []),
      ...(session.specialists ?? []),
    ];
    setSessionLogs(transformLogs(session.session_loggings, participants));
  }, [session]);

  const renderInfoGrid = (items: InfoCardData[]) => (
    <div className={s.infoGrid}>
      {items.map((item) => (
        <div className={s.infoCard} key={item.label}>
          <span className={s.infoLable}>{item.label}</span>
          <span className={s.infoValue}>{item.value}</span>
        </div>
      ))}
    </div>
  );

  const getTagValue = (level: number) =>
    session.tags?.find((tag) => tag.level === level)
      ? getTranslation(session.tags?.find((tag) => tag.level === level)?.labels)
      : "-";

  const primaryDate =
    session.dateAndTime?.date ?? session.datesAndTime?.[0]?.date;
  const primaryDuration =
    session.dateAndTime?.duration ?? session.datesAndTime?.[0]?.duration;
  const participants = [
    ...(session.clients ?? []),
    ...(session.specialists ?? []),
  ];
  const rescheduleHistory = session.rescheduleHistory ?? [];
  const errorLogs = session.errorLogs ?? [];

  return (
    <div className={s.detailsStack}>
      <div className={s.buttonsBlock}>
        <div className={s.backButton} role="button" onClick={onBack}>
          <ArrowLeft />
          <span className={s.backTitle}>Back</span>
        </div>
        {renderActions && (
          <div className={s.actionBlock}>{renderActions()}</div>
        )}
      </div>

      <div className={s.headerBlock}>
        <div className={s.headerTitles}> Session ID: {session._id} </div>

        {session.sessionType && (
          <span className={s.typeBadge}>
            {sessionTypeStrings[session.sessionType] ?? session.sessionType}
          </span>
        )}
      </div>

      {sessionLogs.length > 0 && (
        <div className={s.detailSection}>
          <span className={s.blockLable}>Session logs</span>
          <div className={s.durationBlock}>
            <div className={s.userLogsList}>
              {sessionLogs.map((entry) => (
                <SessionLogs
                  key={entry.participant.userId}
                  entry={entry}
                  sessionDuration={session.sessionDuration}
                />
              ))}
            </div>
          </div>
        </div>
      )}

      <div className={s.detailSection}>
        <div className={s.sectionHeaderRow}>
          <span className={s.blockLable}>Session overview</span>
          {statusBadge && <span className={s.sectionBadge}>{statusBadge}</span>}
        </div>
        {renderInfoGrid([
          ...(session.sessionType
            ? [
                {
                  label: "Session type",
                  value:
                    sessionTypeStrings[session.sessionType] ??
                    session.sessionType,
                },
              ]
            : []),
          ...(primaryDate
            ? [{ label: "Session date", value: formatDisplayDate(primaryDate) }]
            : []),
          ...(primaryDuration
            ? [
                {
                  label: "Planned duration",
                  value: `${primaryDuration} min`,
                },
              ]
            : []),
          ...(session.clientCost != null
            ? [{ label: "Client cost", value: formatMoney(session.clientCost) }]
            : []),
          ...(session.specialistPay != null
            ? [
                {
                  label: "Specialist pay",
                  value: formatMoney(session.specialistPay),
                },
              ]
            : []),
          ...(session.sessionCost != null
            ? [{ label: "Total cost", value: formatMoney(session.sessionCost) }]
            : []),
        ])}

        {session.subject && session.subject.length ? (
          <div className={s.descriptionBlock}>
            <span className={s.infoLable}>Request title</span>
            <p className={s.longText}>{session.subject}</p>
          </div>
        ) : null}

        {session.title && session.title.length ? (
          <div className={s.descriptionBlock}>
            <span className={s.infoLable}>Session title</span>
            <p className={s.longText}>{getTranslation(session.title)}</p>
          </div>
        ) : null}

        {session.description && session.description.length ? (
          <div className={s.descriptionBlock}>
            <span className={s.infoLable}>Session description</span>
            <p className={s.longText}>{getTranslation(session.description)}</p>
          </div>
        ) : null}
      </div>

      {session.tags && session.tags.length > 0 && (
        <div className={s.detailSection}>
          <div className={s.tagsGrid}>
            {tagsSteps.map((item, index) => (
              <div className={s.tagCard} key={item}>
                <span className={s.infoLable}>{item}</span>
                <span className={s.infoValue}>{getTagValue(index + 1)}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {session.packageRef && (
        <div className={s.detailSection}>
          <span className={s.blockLable}>Session package</span>
          {renderInfoGrid([
            {
              label: "Package title",
              value: getTranslation(session.packageRef.title),
            },
          ])}
          {session.title && (
            <div className={s.descriptionBlock}>
              <span className={s.infoLable}>Package session title</span>
              <p className={s.longText}>{getTranslation(session.title)}</p>
            </div>
          )}
          {session.description && (
            <div className={s.descriptionBlock}>
              <span className={s.infoLable}>Package session description</span>
              <p className={s.longText}>
                {getTranslation(session.description)}
              </p>
            </div>
          )}
        </div>
      )}

      {session.project && (
        <div className={s.detailSection}>
          <span className={s.blockLable}>Project</span>
          {renderInfoGrid([
            {
              label: "Project title",
              value: getTranslation(session.project.title),
            },
          ])}
          <div className={s.descriptionBlock}>
            <span className={s.infoLable}>Project description</span>
            <div
              className={s.longText}
              dangerouslySetInnerHTML={{
                __html: getDisplayText(
                  getTranslation(session.project.description),
                ),
              }}
            />
          </div>
        </div>
      )}

      {session.flexPassSubscription && (
        <div className={s.detailSection}>
          <div className={s.sectionHeaderRow}>
            <span className={s.blockLable}>Flex Pass subscription</span>
            <span className={s.sectionBadge}>
              {
                flexPassTypeStrings[
                  session.flexPassSubscription
                    .type as keyof typeof flexPassTypeStrings
                ]
              }
            </span>
          </div>
          {renderInfoGrid([
            {
              label: "Title",
              value: getTranslation(session.flexPassSubscription.title),
            },
            {
              label: "Language",
              value:
                ISO639Lang[session.flexPassSubscription.language] ??
                session.flexPassSubscription.language,
            },
            {
              label: "Start date",
              value: formatDisplayDate(session.flexPassSubscription.startDate),
            },
            {
              label: "End date",
              value: formatDisplayDate(session.flexPassSubscription.endDate),
            },
            {
              label: "Cost",
              value: formatMoney(session.flexPassSubscription.cost),
            },
            {
              label: "Base cost",
              value: formatMoney(session.flexPassSubscription.baseCost),
            },
            {
              label: "Duration",
              value: `${session.flexPassSubscription.duration} days`,
            },
            {
              label: "Universal",
              value: session.flexPassSubscription.isUniversal ? "Yes" : "No",
            },
          ])}

          {session.flexPassSubscription.description && (
            <div className={s.descriptionBlock}>
              <span className={s.infoLable}>Description</span>
              <div
                className={s.longText}
                dangerouslySetInnerHTML={{
                  __html: getDisplayText(
                    getTranslation(session.flexPassSubscription.description),
                  ),
                }}
              />
            </div>
          )}

          {session.flexPassSubscription.type === SubscriptionType.DISCOUNT &&
            renderInfoGrid([
              {
                label: "Session discount",
                value: `${session.flexPassSubscription.sessionDiscount ?? 0}%`,
              },
              {
                label: "Commission",
                value: `${session.flexPassSubscription.talziCommissionPercent ?? 0}%`,
              },
            ])}

          {session.flexPassSubscription.type ===
            SubscriptionType.SESSION_VOLUME &&
            renderInfoGrid([
              {
                label: "Sessions included",
                value: session.flexPassSubscription.amountOfSessions ?? "-",
              },
              {
                label: "First payout",
                value: formatMoney(
                  session.flexPassSubscription.firstSessionPayout,
                ),
              },
              {
                label: "Repeat payout",
                value: formatMoney(
                  session.flexPassSubscription.repeatSessionPayout,
                ),
              },
            ])}
        </div>
      )}

      {rescheduleHistory.length > 0 && (
        <div className={s.detailSection}>
          <span className={s.blockLable}>Reschedule history</span>
          <div className={s.listStack}>
            {rescheduleHistory.map((historyItem, index) => (
              <div
                className={s.timelineItem}
                key={`${historyItem.invokedAt}-${index}`}
              >
                <div className={s.sectionHeaderRow}>
                  <span className={s.itemTitle}>Reschedule #{index + 1}</span>
                  <span className={s.sectionBadge}>
                    {historyItem.rule.type}
                  </span>
                </div>
                {renderInfoGrid([
                  {
                    label: "Invoked by",
                    value: getParticipantDisplayName(
                      historyItem.invokedBy,
                      participants,
                    ),
                  },
                  {
                    label: "Invoked at",
                    value: formatDisplayDateTime(historyItem.invokedAt),
                  },
                  {
                    label: "Previous date",
                    value: formatDisplayDateTime(historyItem.previousDate),
                  },
                  {
                    label: "New date",
                    value: formatDisplayDateTime(historyItem.newDate),
                  },
                  {
                    label: "Rule for",
                    value: historyItem.rule.ruleFor,
                  },
                  {
                    label: "Threshold passed",
                    value: historyItem.isThresholdPassed ? "Yes" : "No",
                  },
                  {
                    label: "Hours threshold",
                    value: `${historyItem.rule.hoursThreshold} h`,
                  },
                  {
                    label: "Static rule",
                    value: historyItem.rule.static ? "Yes" : "No",
                  },
                ])}
              </div>
            ))}
          </div>
        </div>
      )}

      {errorLogs.length > 0 && (
        <div className={s.detailSection}>
          <div className={s.sectionHeaderRow}>
            <span className={s.blockLable}>Error logs</span>
            <button
              className={s.toggleButton}
              type="button"
              onClick={() => setIsErrorLogsOpen((prev) => !prev)}
            >
              {isErrorLogsOpen ? "Collapse" : "Expand"} ({errorLogs.length})
            </button>
          </div>

          {isErrorLogsOpen && (
            <div className={s.listStack}>
              {errorLogs.map((log, index) => (
                <div className={s.timelineItem} key={`${log.traceId}-${index}`}>
                  <div className={s.sectionHeaderRow}>
                    <span className={s.itemTitle}>Error #{index + 1}</span>
                    <span className={s.sectionBadge}>{log.traceId}</span>
                  </div>
                  {renderInfoGrid([
                    {
                      label: "Date",
                      value: formatDisplayDateTime(log.date),
                    },
                    {
                      label: "User",
                      value: getParticipantDisplayName(log.user, participants),
                    },
                    {
                      label: "Session",
                      value: log.session ?? "-",
                    },
                    {
                      label: "Message",
                      value: log.message,
                    },
                  ])}
                  {log.customComment && (
                    <div className={s.descriptionBlock}>
                      <span className={s.infoLable}>Custom comment</span>
                      <p className={s.longText}>{log.customComment}</p>
                    </div>
                  )}
                  <div className={s.descriptionBlock}>
                    <span className={s.infoLable}>Stack</span>
                    <pre className={s.stackTrace}>{formatStack(log.stack)}</pre>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default SessionInfoBlock;
